import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Tag, Space, Modal, Radio, Typography, message, Tooltip, Empty } from 'antd';
import dayjs from 'dayjs';
import {
  listCancellations, decidePOLineCancel, decideMRLineCancel, updatePOStatus,
  type CancellationRow, type CancellationQueueResult, type POLineMrAction,
} from '../api';

const { Text } = Typography;

const fmt = (v: number) => Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const TYPE_LABEL: Record<CancellationRow['type'], { label: string; color: string }> = {
  po: { label: 'Whole PO', color: 'red' },
  po_line: { label: 'PO line', color: 'orange' },
  mr_line: { label: 'MR line', color: 'blue' },
};
const outcomeLabel = (a: POLineMrAction | null) => (a === 'reorder' ? 'order it again' : a === 'not_needed' ? 'no longer needed' : '');
const ageText = (h: number | null) => (h == null ? '-' : h < 1 ? 'just now' : h < 24 ? `${h} h` : `${Math.floor(h / 24)} d`);

/** Decide one queue row through the route that owns it. Returns nothing; throws on failure. */
async function decideRow(row: CancellationRow, decision: 'approve' | 'reject', mrAction?: POLineMrAction) {
  if (row.type === 'po') {
    await updatePOStatus(row.document, decision === 'approve' ? 'Cancelled' : 'Ordered', undefined, decision === 'approve' ? mrAction || row.mrAction || undefined : undefined);
  } else if (row.type === 'po_line') {
    await decidePOLineCancel(row.document, row.lineId!, { decision, mrAction: decision === 'approve' ? mrAction || row.mrAction || undefined : undefined });
  } else {
    await decideMRLineCancel(row.document, row.lineId!, { decision });
  }
}

interface Props {
  /** 'pending' shows the queue with actions; 'decided' shows history. */
  mode?: 'pending' | 'decided';
  /** Compact panel on the dashboard: first N rows, no filters, link to the full page. */
  limit?: number;
  type?: CancellationRow['type'];
  onLoaded?: (result: CancellationQueueResult) => void;
  /** Called after any decision so the parent can refresh its own numbers. */
  onChanged?: () => void;
  showBulk?: boolean;
}

const CancellationQueue: React.FC<Props> = ({ mode = 'pending', limit, type, onLoaded, onChanged, showBulk = true }) => {
  const navigate = useNavigate();
  const [result, setResult] = useState<CancellationQueueResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<Record<string, POLineMrAction>>({});
  const [selected, setSelected] = useState<React.Key[]>([]);
  const [confirm, setConfirm] = useState<{ rows: CancellationRow[]; decision: 'approve' | 'reject' } | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  // onLoaded is usually an inline arrow; keeping it out of load's deps stops the fetch → setState → new
  // callback → fetch loop that hammered /api/cancellations from the full page (2026-09-24).
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listCancellations({ status: mode, limit, type });
      setResult(r);
      onLoadedRef.current?.(r);
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to load cancellations');
    } finally {
      setLoading(false);
    }
  }, [mode, limit, type]);

  useEffect(() => { load(); }, [load]);

  const rows = result?.rows || [];
  const canDecide = !!result?.canDecide && mode === 'pending';
  const chosen = (r: CancellationRow): POLineMrAction | undefined => outcome[r.key] || r.mrAction || undefined;

  const runDecisions = async (list: CancellationRow[], decision: 'approve' | 'reject') => {
    setBulkBusy(true);
    const failures: string[] = [];
    let done = 0;
    for (const r of list) {
      try {
        await decideRow(r, decision, chosen(r));
        done += 1;
      } catch (err: any) {
        failures.push(`${r.document}${r.lineNo ? ` line ${r.lineNo}` : ''}: ${err.response?.data?.error || err.message || 'failed'}`);
      }
    }
    setBulkBusy(false);
    setConfirm(null);
    setSelected([]);
    if (failures.length === 0) message.success(`${done} ${decision === 'approve' ? 'approved' : 'rejected'}`);
    else Modal.warning({ title: `${done} done, ${failures.length} failed`, content: <ul style={{ paddingLeft: 18 }}>{failures.map((f) => <li key={f}>{f}</li>)}</ul> });
    load();
    onChanged?.();
  };

  const single = async (r: CancellationRow, decision: 'approve' | 'reject') => {
    if (decision === 'approve') { setConfirm({ rows: [r], decision }); return; }
    setBusyKey(r.key);
    try {
      await decideRow(r, 'reject');
      message.success(`${r.document}${r.lineNo ? ` line ${r.lineNo}` : ''}: rejected, kept as is`);
      load();
      onChanged?.();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to record the decision');
    } finally {
      setBusyKey(null);
    }
  };

  const columns = useMemo(() => {
    const cols: any[] = [
      { title: 'Type', key: 'type', width: 92, render: (_: any, r: CancellationRow) => <Tag color={TYPE_LABEL[r.type].color} style={{ marginInlineEnd: 0 }}>{TYPE_LABEL[r.type].label}</Tag> },
      {
        title: 'Document', key: 'doc', width: 150,
        render: (_: any, r: CancellationRow) => (
          <Button type="link" size="small" style={{ padding: 0, fontFamily: 'ui-monospace, Menlo, monospace' }} onClick={() => navigate(r.documentPath)}>
            {r.document}{r.lineNo ? ` · L${r.lineNo}` : ''}
          </Button>
        ),
      },
      {
        title: 'Item', key: 'item', ellipsis: true,
        render: (_: any, r: CancellationRow) => (
          <span>
            {r.item.code ? <Text code style={{ fontSize: 12 }}>{r.item.code}</Text> : r.type !== 'po' ? <Text type="secondary" style={{ fontSize: 11 }}>free text</Text> : null}{' '}
            <span>{r.item.description}</span>
            {r.mrRef && <div style={{ fontSize: 11, color: '#8a94a6' }}>{r.mrRef}</div>}
          </span>
        ),
      },
      {
        title: 'Qty', key: 'qty', width: 120, align: 'right' as const,
        render: (_: any, r: CancellationRow) => r.type === 'po' ? <span>{r.qty} of {r.ofQty}</span> : <span>{r.qty} of {r.ofQty} {r.unit || ''}{r.received > 0 && <div style={{ fontSize: 11, color: '#8a94a6' }}>{r.received} received</div>}</span>,
      },
      { title: 'Value EGP', key: 'value', width: 110, align: 'right' as const, render: (_: any, r: CancellationRow) => fmt(r.value) },
      { title: 'Requested by', dataIndex: 'requestedBy', key: 'by', width: 130, ellipsis: true },
      {
        title: mode === 'pending' ? 'Age' : 'Decided', key: 'age', width: 90,
        render: (_: any, r: CancellationRow) => mode === 'pending'
          ? <span style={{ color: (r.ageHours ?? 0) >= 24 ? '#cf1322' : undefined, fontWeight: (r.ageHours ?? 0) >= 24 ? 600 : undefined }}>{ageText(r.ageHours)}</span>
          : <span>{r.decidedAt ? dayjs(r.decidedAt).format('DD MMM HH:mm') : '-'}<div style={{ fontSize: 11, color: '#8a94a6' }}>{r.decidedBy || ''}</div></span>,
      },
      { title: 'Reason', dataIndex: 'reason', key: 'reason', ellipsis: true, render: (v: string) => <Tooltip title={v}>{v}</Tooltip> },
      {
        title: 'MR outcome', key: 'outcome', width: 190,
        render: (_: any, r: CancellationRow) => {
          if (r.type === 'mr_line') return <Text type="secondary" style={{ fontSize: 12 }}>not ordered, line closes</Text>;
          if (!canDecide) return <span style={{ fontSize: 12 }}>{outcomeLabel(chosen(r) || null)}</span>;
          return (
            <Radio.Group
              size="small"
              optionType="button"
              value={chosen(r)}
              onChange={(e) => setOutcome((o) => ({ ...o, [r.key]: e.target.value }))}
              options={[{ label: 'Not needed', value: 'not_needed' }, { label: 'Order again', value: 'reorder' }]}
            />
          );
        },
      },
    ];
    if (mode === 'decided') {
      cols.splice(0, 0, { title: '', key: 'status', width: 92, render: (_: any, r: CancellationRow) => <Tag color={r.status === 'Approved' ? 'red' : 'default'}>{r.status}</Tag> });
    }
    if (canDecide) {
      cols.push({
        title: '', key: 'acts', width: 170, fixed: 'right' as const,
        render: (_: any, r: CancellationRow) => (
          <Space size={6}>
            <Button size="small" loading={busyKey === r.key} onClick={() => single(r, 'reject')}>Reject</Button>
            <Button size="small" type="primary" danger onClick={() => single(r, 'approve')}>Approve</Button>
          </Space>
        ),
      });
    }
    return cols;
  }, [mode, canDecide, busyKey, outcome, navigate]);

  const selectedRows = rows.filter((r) => selected.includes(r.key));
  const selectedValue = selectedRows.reduce((s, r) => s + r.value, 0);

  return (
    <div>
      {canDecide && showBulk && rows.length > 1 && (
        <Space style={{ marginBottom: 10 }} wrap>
          <Text type="secondary" style={{ fontSize: 12 }}>{selected.length ? `${selected.length} selected · ${fmt(selectedValue)} EGP` : 'Tick rows to decide several at once'}</Text>
          <Button size="small" disabled={!selected.length} onClick={() => setConfirm({ rows: selectedRows, decision: 'reject' })}>Reject selected</Button>
          <Button size="small" type="primary" danger disabled={!selected.length} onClick={() => setConfirm({ rows: selectedRows, decision: 'approve' })}>Approve selected{selected.length ? ` (${selected.length})` : ''}</Button>
        </Space>
      )}
      <Table
        size="small"
        loading={loading}
        rowKey="key"
        columns={columns}
        dataSource={rows}
        pagination={false}
        scroll={{ x: 1180 }}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={mode === 'pending' ? 'Nothing waiting for a decision' : 'Nothing decided in the last 30 days'} /> }}
        rowSelection={canDecide && showBulk ? { selectedRowKeys: selected, onChange: setSelected, checkStrictly: true, getCheckboxProps: (r: CancellationRow) => ({ disabled: r.key.startsWith('po_line:') && rows.some((p) => p.children?.some((c) => c.key === r.key)) }) } : undefined}
        expandable={{
          rowExpandable: (r: CancellationRow) => !!r.children?.length,
          expandedRowRender: (r: CancellationRow) => (
            <Table
              size="small"
              rowKey="key"
              pagination={false}
              showHeader={false}
              dataSource={r.children}
              columns={[
                { key: 'l', width: 92, render: () => <Text type="secondary" style={{ fontSize: 12 }}>line</Text> },
                { key: 'doc', width: 150, render: (_: any, c: CancellationRow) => <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12 }}>L{c.lineNo}</span> },
                { key: 'item', ellipsis: true, render: (_: any, c: CancellationRow) => <span>{c.item.code ? <Text code style={{ fontSize: 12 }}>{c.item.code}</Text> : null} {c.item.description}{c.mrRef && <span style={{ fontSize: 11, color: '#8a94a6' }}> · {c.mrRef}</span>}</span> },
                { key: 'qty', width: 120, align: 'right' as const, render: (_: any, c: CancellationRow) => <span>{c.qty} of {c.ofQty} {c.unit || ''}{c.received > 0 ? ` · ${c.received} received` : ''}</span> },
                { key: 'value', width: 110, align: 'right' as const, render: (_: any, c: CancellationRow) => fmt(c.value) },
              ]}
            />
          ),
        }}
      />
      {result?.truncated && (
        <div style={{ marginTop: 8, fontSize: 12 }}>
          <Text type="secondary">Showing the first {rows.length} of {result.summary.count}. </Text>
          <Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate('/cancellations')}>Open full list</Button>
        </div>
      )}

      <Modal
        title={confirm ? `${confirm.decision === 'approve' ? 'Approve' : 'Reject'} ${confirm.rows.length === 1 ? 'cancellation' : `${confirm.rows.length} cancellations`}` : ''}
        open={!!confirm}
        onOk={() => confirm && runDecisions(confirm.rows, confirm.decision)}
        onCancel={() => setConfirm(null)}
        confirmLoading={bulkBusy}
        okText={confirm?.decision === 'approve' ? (confirm.rows.length === 1 ? 'Approve cancellation' : `Approve all ${confirm.rows.length}`) : 'Reject, keep as is'}
        okButtonProps={{ danger: confirm?.decision === 'approve' }}
        width={640}
        destroyOnClose
      >
        {confirm && (
          <div>
            <ul style={{ paddingLeft: 18, margin: '4px 0 10px' }}>
              {confirm.rows.map((r) => (
                <li key={r.key} style={{ marginBottom: 4 }}>
                  <strong>{r.document}{r.lineNo ? ` line ${r.lineNo}` : ''}</strong> · {TYPE_LABEL[r.type].label.toLowerCase()} · {r.qty}{r.unit ? ` ${r.unit}` : ''} · {fmt(r.value)} EGP
                  {r.type !== 'mr_line' && confirm.decision === 'approve' && <> · {outcomeLabel(chosen(r) || null)}</>}
                  {r.type === 'mr_line' && confirm.decision === 'approve' && <> · MR line closes</>}
                  {r.children?.length ? <div style={{ fontSize: 12, color: '#8a94a6' }}>{r.children.length} line{r.children.length === 1 ? '' : 's'}: {r.children.map((c) => `L${c.lineNo} ${c.qty}${c.unit ? ` ${c.unit}` : ''}`).join(', ')}</div> : null}
                </li>
              ))}
            </ul>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {confirm.decision === 'approve'
                ? `${fmt(confirm.rows.reduce((s, r) => s + r.value, 0))} EGP of open value. Each request is applied one by one; if one fails you see which, and the rest still go through.`
                : 'Nothing changes on the records; the requests are marked rejected and can be raised again.'}
            </Text>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CancellationQueue;
